import type { SpeciesDef, SpeciesId } from "./species.ts";

/**
 * Sidechain bestiary (Lv38-50): Halving Harvest, Ghostchain, Pump Jungle, Sakura Stake and the
 * Rugpull Ruins. Base forms sit at ~280-300 total stats, finals at ~370-400, the two legends at
 * 430. Original IP only.
 */
export const SIDECHAIN_SPECIES_IDS = [
  "pumpkoin", "gourdian", "crowlet", "crowllateral", "nutling", "nutvault",
  "rektling", "rektoplasm", "tombstonk", "cryptkeep", "webthree", "arachnft", "deadcat", "catacomb",
  "toucoin", "beakfolio", "vinesnek", "vineconda", "slothlet", "slothstake",
  "koinfish", "ryukoin", "origamint", "craneprint", "sproutle", "bonsaitoise",
  "golemint", "colosstake", "riddlekit", "sphynxfi", "wraplet", "wrappharaoh",
  "arborealm", "rektquiem",
] as const;

type Def = Omit<SpeciesDef, "id">;
const sp = (id: SpeciesId, d: Def): SpeciesDef => ({ id, ...d });

export const SIDECHAIN_SPECIES = {
  // ---- Halving Harvest ----------------------------------------------------------------------
  pumpkoin: sp("pumpkoin", {
    name: "Pumpkoin", types: ["stake", "burn"], hp: 72, atk: 70, def: 76, spd: 58, catch: 40, bounty: 40,
    evolvesTo: "gourdian", evolveLevel: 42,
    moves: ["vineLash", "lanternFlare", "seedRound", "harvestRewards"],
    blurb: "Carved its own grin at the last halving. Glows brighter every time the reward shrinks.",
    cry: "pomp",
  }),
  gourdian: sp("gourdian", {
    name: "Gourdian", types: ["stake", "rekt"], hp: 96, atk: 96, def: 104, spd: 64, catch: 20, bounty: 62,
    moves: ["petalStorm", "lanternFlare", "liquidation", "harvestRewards"],
    moveUpgrades: { vineLash: "petalStorm", seedRound: "liquidation" },
    blurb: "Guards the patch after dark. The candle inside has never once gone out.",
    cry: "GOOOURD",
  }),
  crowlet: sp("crowlet", {
    name: "Crowlet", types: ["rekt", "flex"], hp: 58, atk: 76, def: 54, spd: 92, catch: 40, bounty: 38,
    evolvesTo: "crowllateral", evolveLevel: 42,
    moves: ["spook", "paperCrane", "paperHands", "deadCatBounce"],
    blurb: "Steals shiny coins from scarecrows and posts them as its own gains.",
    cry: "kraw",
  }),
  crowllateral: sp("crowllateral", {
    name: "Crowllateral", types: ["rekt", "flex"], hp: 80, atk: 108, def: 72, spd: 112, catch: 18, bounty: 60,
    moves: ["blackCandle", "beakFlex", "paperHands", "liquidation"],
    moveUpgrades: { spook: "blackCandle", paperCrane: "beakFlex", deadCatBounce: "liquidation" },
    blurb: "Borrows against every shiny thing it owns. When the market dips, it collects.",
    cry: "KRAAAW",
  }),
  nutling: sp("nutling", {
    name: "Nutling", types: ["hodl", "stake"], hp: 66, atk: 62, def: 84, spd: 70, catch: 42, bounty: 36,
    evolvesTo: "nutvault", evolveLevel: 41,
    moves: ["sproutJab", "acornStash", "frostNibble", "seedRound"],
    blurb: "Buries one acorn per block. Forgets where. The forest grows anyway.",
    cry: "nik nik",
  }),
  nutvault: sp("nutvault", {
    name: "Nutvault", types: ["hodl", "stake"], hp: 90, atk: 92, def: 112, spd: 78, catch: 20, bounty: 58,
    moves: ["monolithSlam", "acornStash", "yieldFarm", "iceAge"],
    moveUpgrades: { sproutJab: "yieldFarm", frostNibble: "monolithSlam", seedRound: "iceAge" },
    blurb: "Its cheeks hold a whole winter. It has never sold a single nut.",
    cry: "NUTT",
  }),
  // ---- Ghostchain ---------------------------------------------------------------------------
  rektling: sp("rektling", {
    name: "Rektling", types: ["rekt"], hp: 60, atk: 72, def: 60, spd: 88, catch: 40, bounty: 38,
    evolvesTo: "rektoplasm", evolveLevel: 43,
    moves: ["spook", "hauntedChart", "deathSpiral", "deadCatBounce"],
    blurb: "What is left of a coin that went to zero. It still checks the price, just in case.",
    cry: "boooo",
  }),
  rektoplasm: sp("rektoplasm", {
    name: "Rektoplasm", types: ["rekt", "viral"], hp: 84, atk: 104, def: 80, spd: 110, catch: 18, bounty: 60,
    moves: ["hauntedChart", "toxicThread", "deathSpiral", "capitulation"],
    moveUpgrades: { spook: "toxicThread", deadCatBounce: "capitulation" },
    blurb: "A haunting that went viral. Everybody who sees the chart feels a chill.",
    cry: "BWOOOOO",
  }),
  tombstonk: sp("tombstonk", {
    name: "Tombstonk", types: ["rekt", "hodl"], hp: 80, atk: 70, def: 98, spd: 36, catch: 38, bounty: 40,
    evolvesTo: "cryptkeep", evolveLevel: 44,
    moves: ["graveyardShift", "liquidation", "stalactite", "paperHands"],
    blurb: "A headstone that only goes up. Nobody remembers what it was holding.",
    cry: "donk",
  }),
  cryptkeep: sp("cryptkeep", {
    name: "Cryptkeep", types: ["rekt", "hodl"], hp: 108, atk: 98, def: 124, spd: 44, catch: 18, bounty: 64,
    moves: ["graveyardShift", "blackCandle", "monolithSlam", "exitLiquidity"],
    moveUpgrades: { liquidation: "blackCandle", stalactite: "monolithSlam", paperHands: "exitLiquidity" },
    blurb: "Keeps the keys to every lost wallet in the graveyard. Will not tell you where.",
    cry: "KRRRYPT",
  }),
  webthree: sp("webthree", {
    name: "Webthree", types: ["signal", "rekt"], hp: 60, atk: 78, def: 62, spd: 84, catch: 40, bounty: 40,
    evolvesTo: "arachnft", evolveLevel: 43,
    moves: ["pingSpike", "spook", "packetLoss", "deadCatBounce"],
    blurb: "Spins a decentralized web. Every thread is a node. Every node is sticky.",
    cry: "tik tik",
  }),
  arachnft: sp("arachnft", {
    name: "Arachnft", types: ["flex", "rekt"], hp: 82, atk: 108, def: 84, spd: 102, catch: 18, bounty: 62,
    moves: ["airdropStrike", "hauntedChart", "packetLoss", "liquidation"],
    moveUpgrades: { pingSpike: "airdropStrike", spook: "hauntedChart", deadCatBounce: "liquidation" },
    blurb: "Each of its eight legs is a one-of-one. The web is the floor price.",
    cry: "SKITTT",
  }),
  deadcat: sp("deadcat", {
    name: "Deadcat", types: ["rekt", "cope"], hp: 64, atk: 76, def: 62, spd: 82, catch: 40, bounty: 38,
    evolvesTo: "catacomb", evolveLevel: 44,
    moves: ["deadCatBounce", "napTime", "spook", "doomscroll"],
    blurb: "Fell from the top of the chart and bounced. Twice. It is fine, it says.",
    cry: "mrrrow",
  }),
  catacomb: sp("catacomb", {
    name: "Catacomb", types: ["rekt", "cope"], hp: 92, atk: 106, def: 86, spd: 94, catch: 18, bounty: 62,
    moves: ["deadCatBounce", "exitLiquidity", "doomscroll", "capitulation"],
    moveUpgrades: { napTime: "exitLiquidity", spook: "capitulation" },
    blurb: "Nine lives, all spent on the same trade. Rules the tunnels under the graves.",
    cry: "MRRROWWW",
  }),
  // ---- Pump Jungle --------------------------------------------------------------------------
  toucoin: sp("toucoin", {
    name: "Toucoin", types: ["flex", "stake"], hp: 62, atk: 80, def: 58, spd: 90, catch: 40, bounty: 40,
    evolvesTo: "beakfolio", evolveLevel: 45,
    moves: ["paperCrane", "seedRound", "vineLash", "flexFlurry"],
    blurb: "Its beak is bigger than its bags. It shows both off to everyone.",
    cry: "tuka!",
  }),
  beakfolio: sp("beakfolio", {
    name: "Beakfolio", types: ["flex", "stake"], hp: 84, atk: 112, def: 76, spd: 110, catch: 18, bounty: 62,
    moves: ["beakFlex", "petalStorm", "airdropStrike", "compoundGrowth"],
    moveUpgrades: { paperCrane: "beakFlex", vineLash: "petalStorm", flexFlurry: "airdropStrike" },
    blurb: "Every stripe on its beak is a different winning position. It diversified early.",
    cry: "TUKAAA",
  }),
  vinesnek: sp("vinesnek", {
    name: "Vinesnek", types: ["stake"], hp: 64, atk: 78, def: 64, spd: 84, catch: 40, bounty: 40,
    evolvesTo: "vineconda", evolveLevel: 45,
    moves: ["vineLash", "rootLock", "yieldFarm", "sproutJab"],
    blurb: "Looks exactly like a vine until it staked you. Then it looks smug.",
    cry: "sssip",
  }),
  vineconda: sp("vineconda", {
    name: "Vineconda", types: ["stake", "rug"], hp: 92, atk: 110, def: 88, spd: 94, catch: 18, bounty: 64,
    moves: ["petalStorm", "rootLock", "yieldFarm", "rugPull"],
    moveUpgrades: { vineLash: "petalStorm", sproutJab: "rugPull" },
    blurb: "Wraps the whole canopy and squeezes out the liquidity. Leaves only leaves.",
    cry: "SSSSSQUEEZ",
  }),
  slothlet: sp("slothlet", {
    name: "Slothlet", types: ["stake", "cope"], hp: 90, atk: 66, def: 84, spd: 32, catch: 42, bounty: 38,
    evolvesTo: "slothstake", evolveLevel: 46,
    moves: ["hammock", "sproutJab", "lockup", "napTime"],
    blurb: "Staked itself to a branch years ago. The rewards have been lovely.",
    cry: "...hm",
  }),
  slothstake: sp("slothstake", {
    name: "Slothstake", types: ["stake", "cope"], hp: 124, atk: 96, def: 112, spd: 36, catch: 18, bounty: 62,
    moves: ["oldGrowth", "hammock", "lockup", "compoundGrowth"],
    moveUpgrades: { sproutJab: "oldGrowth", napTime: "compoundGrowth" },
    blurb: "Moss grows on its moss. It has never unstaked and never will.",
    cry: "......HMMM",
  }),
  // ---- Sakura Stake -------------------------------------------------------------------------
  koinfish: sp("koinfish", {
    name: "Koinfish", types: ["liquid", "stake"], hp: 62, atk: 70, def: 66, spd: 86, catch: 40, bounty: 40,
    evolvesTo: "ryukoin", evolveLevel: 46,
    moves: ["koiCurrent", "sproutJab", "riptide", "pearlShield"],
    blurb: "Its scales are old coins from a forgotten mint. It swims upstream on principle.",
    cry: "blup",
  }),
  ryukoin: sp("ryukoin", {
    name: "Ryukoin", types: ["liquid", "moon"], hp: 90, atk: 110, def: 88, spd: 104, catch: 16, bounty: 66,
    moves: ["koiCurrent", "moonbeam", "petalStorm", "wenMoon"],
    moveUpgrades: { sproutJab: "petalStorm", riptide: "moonbeam", pearlShield: "wenMoon" },
    blurb: "Climbed the waterfall, then the sky. Its whiskers trail blossoms and candles.",
    cry: "RYUUUUU",
  }),
  origamint: sp("origamint", {
    name: "Origamint", types: ["flex"], hp: 58, atk: 74, def: 60, spd: 92, catch: 40, bounty: 38,
    evolvesTo: "craneprint", evolveLevel: 46,
    moves: ["paperCrane", "flexCloud", "seedRound", "quickFlip"],
    blurb: "Folded from a winning receipt. Every crease is a trade that worked.",
    cry: "fsst",
  }),
  craneprint: sp("craneprint", {
    name: "Craneprint", types: ["flex", "moon"], hp: 82, atk: 104, def: 80, spd: 118, catch: 16, bounty: 62,
    moves: ["paperCrane", "moonbeam", "flexCloud", "airdropStrike"],
    moveUpgrades: { seedRound: "moonbeam", quickFlip: "airdropStrike" },
    blurb: "A thousand folds of fresh print. It glides straight past the money printer.",
    cry: "KRRRANE",
  }),
  sproutle: sp("sproutle", {
    name: "Sproutle", types: ["stake"], hp: 72, atk: 64, def: 86, spd: 48, catch: 42, bounty: 36,
    evolvesTo: "bonsaitoise", evolveLevel: 46,
    moves: ["sproutJab", "acornStash", "rootLock", "seedRound"],
    blurb: "Carries a sapling on its shell and waters it every morning. Patient, very patient.",
    cry: "tup",
  }),
  bonsaitoise: sp("bonsaitoise", {
    name: "Bonsaitoise", types: ["stake", "hodl"], hp: 104, atk: 94, def: 126, spd: 50, catch: 16, bounty: 64,
    moves: ["oldGrowth", "monolithSlam", "rootLock", "harvestRewards"],
    moveUpgrades: { sproutJab: "oldGrowth", acornStash: "monolithSlam", seedRound: "harvestRewards" },
    blurb: "A pruned tree three hundred years old grows on its back. Both are still staking.",
    cry: "TOOOORT",
  }),
  // ---- Rugpull Ruins ------------------------------------------------------------------------
  golemint: sp("golemint", {
    name: "Golemint", types: ["hodl"], hp: 82, atk: 82, def: 100, spd: 34, catch: 38, bounty: 42,
    evolvesTo: "colosstake", evolveLevel: 48,
    moves: ["monolithSlam", "diamondGrip", "stalactite", "frostNibble"],
    blurb: "Minted from temple stone. It has held the same position since the first block.",
    cry: "grnnd",
  }),
  colosstake: sp("colosstake", {
    name: "Colosstake", types: ["hodl", "stake"], hp: 112, atk: 118, def: 128, spd: 40, catch: 14, bounty: 70,
    moves: ["monolithSlam", "oldGrowth", "diamondGrip", "permafrost"],
    moveUpgrades: { stalactite: "oldGrowth", frostNibble: "permafrost" },
    blurb: "A statue so old that forests staked on its shoulders. It woke up for this.",
    cry: "GROOOOND",
  }),
  riddlekit: sp("riddlekit", {
    name: "Riddlekit", types: ["chaos"], hp: 64, atk: 72, def: 64, spd: 94, catch: 40, bounty: 40,
    evolvesTo: "sphynxfi", evolveLevel: 47,
    moves: ["riddle", "rumorMill", "spook", "flashCrash"],
    blurb: "Asks every visitor a riddle. The answer is always the same: it depends.",
    cry: "mew?",
  }),
  sphynxfi: sp("sphynxfi", {
    name: "Sphynxfi", types: ["chaos", "rekt"], hp: 90, atk: 108, def: 90, spd: 112, catch: 14, bounty: 70,
    moves: ["riddle", "mempoolCrush", "blackCandle", "blackSwan"],
    moveUpgrades: { rumorMill: "mempoolCrush", spook: "blackCandle", flashCrash: "blackSwan" },
    blurb: "Guards the ruin's last vault with a riddle nobody has ever answered on-chain.",
    cry: "MRRRAAH",
  }),
  wraplet: sp("wraplet", {
    name: "Wraplet", types: ["rekt", "rug"], hp: 70, atk: 76, def: 76, spd: 70, catch: 38, bounty: 42,
    evolvesTo: "wrappharaoh", evolveLevel: 48,
    moves: ["unwrap", "spook", "spreadFud", "graveyardShift"],
    blurb: "A wrapped token that has been wrapped so long it forgot what was inside.",
    cry: "mmmf",
  }),
  wrappharaoh: sp("wrappharaoh", {
    name: "Wrappharaoh", types: ["rekt", "rug"], hp: 98, atk: 112, def: 104, spd: 82, catch: 14, bounty: 70,
    moves: ["unwrap", "capitulation", "spreadFud", "phantomMint"],
    moveUpgrades: { spook: "capitulation", graveyardShift: "phantomMint" },
    blurb: "Ruled a chain that no longer exists. Still collects tribute in its own token.",
    cry: "MMMMMFARAH",
  }),
  // ---- Legends ------------------------------------------------------------------------------
  arborealm: sp("arborealm", {
    name: "Arborealm", types: ["stake", "moon"], hp: 112, atk: 110, def: 106, spd: 102, catch: 6, bounty: 140,
    moves: ["oldGrowth", "moonbeam", "harvestRewards", "compoundGrowth"],
    blurb: "The stag whose antlers are the garden's oldest blossom tree. Seen once per halving.",
    cry: "HWOOOOORN",
  }),
  rektquiem: sp("rektquiem", {
    name: "Rektquiem", types: ["rekt", "chaos"], hp: 106, atk: 116, def: 100, spd: 108, catch: 6, bounty: 140,
    moves: ["capitulation", "blackSwan", "deathSpiral", "exitLiquidity"],
    blurb: "The ghost of the first chain ever rugged. It plays the ruins' last song on repeat.",
    cry: "REEEKT",
  }),
} satisfies Partial<Record<SpeciesId, SpeciesDef>>;
